import { Button, Card, CardContent, CardHeader, Grid, Typography } from "@mui/material";
import { Fragment, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AccountDetailsExplanation } from "@common/Layout/AccountDetailsExplanation";
import { AccountDetailsLayout } from "@common/Layout/AccountDetailsLayout";
import { BRAND_NAME, TITLE_INHERIT_STYLE } from "@constants";
import moveMyNumber from "@static/img/portin/moveMyNumber.png";
import { PortInEdit } from "@features/PortIn/PortInEdit";
import { PortInDetails } from "@features/PortIn/PortInDetails";
import { PortInAction } from "@features/PortIn/PortInAction";
import { PortInState } from "@features/PortIn/interface/IPortIn";
import { useGetPortIn } from "@features/PortIn/hook/useGetPortIn";
import { SelectedServiceContext } from "@context/ISelectedContext";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { useStyle } from "@pages/PortIn/css/style";

export const PortInPage = () => {
    const { selectedService } = useContext(SelectedServiceContext);
    const { t } = useTranslation(["accountDetails", "portin"]);

    const {
        getPortInState: { error, isLoading },
        getPortIn,
        setIsPortOpen,
        isPortOpen,
        portInState,
        previousPhoneNumber,
        portInDetails,
    } = useGetPortIn();

    useEffect(() => {
        getPortInDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedService]);

    const hasDetails = portInState === PortInState.SCHEDULED || portInState === PortInState.IN_PROGRESS;

    const handlePortIn = () => setIsPortOpen(true);
    const getPortInDetails = () => {
        if (selectedService) {
            getPortIn(selectedService.id);
        }
    };

    const closePortIn = () => {
        setIsPortOpen(false);
    };

    const { classes } = useStyle();

    return (
        <AccountDetailsLayout
            hasDefault={false}
            requireNumberSelection
            selectYourNumberTitle={t("portin:wantMoveNumber")}
            title={t("accountDetails:moveNumber")}
        >
            <Grid container direction="column">
                <Grid item>
                    <AccountDetailsExplanation
                        alt="move my number"
                        src={moveMyNumber}
                        texts={[t("portin:portInStep1"), t("portin:portInStep2"), t("portin:portInState3")]}
                        title={t("portin:portInExplanationTitle")}
                    />
                </Grid>
                {selectedService && (
                    <Grid className={classes.gridContainer} item>
                        <Card elevation={2}>
                            <CardHeader
                                className="card-header card-header__right"
                                title={t("portin:moveStatus")}
                                titleTypographyProps={TITLE_INHERIT_STYLE}
                            />
                            <CardContent>
                                <div className="card-header__circle" />
                                <HandleErrorAndLoading
                                    error={error}
                                    isLoading={isLoading}
                                    retryFetch={getPortInDetails}
                                >
                                    {isPortOpen && (
                                        <PortInEdit
                                            closePortIn={closePortIn}
                                            getPortIn={getPortIn}
                                            portInDetails={portInDetails}
                                            previousPhoneNumber={previousPhoneNumber}
                                            selectedService={selectedService}
                                        />
                                    )}
                                    {!isPortOpen && (
                                        <Fragment>
                                            {portInState === PortInState.INITIAL && (
                                                <Grid container justifyContent="center" spacing={4}>
                                                    <Grid item xs={12}>
                                                        <Typography align="center" color="secondary" variant="normal">
                                                            {t("portin:scheduleMoveNumber")}
                                                            <br />
                                                            {t("portin:replacePreviousNumber", {
                                                                brand: BRAND_NAME,
                                                                previousNumber: selectedService.name,
                                                            })}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Button
                                                            color="secondary"
                                                            size="large"
                                                            variant="outlined"
                                                            onClick={handlePortIn}
                                                        >
                                                            {t("portin:scheduleNow")}
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            )}
                                            {hasDetails && (
                                                <Grid container justifyContent="center" spacing={2}>
                                                    <Grid item xs={12}>
                                                        <PortInDetails
                                                            portInDateTime={portInDetails?.portStartDateTime}
                                                            portInState={portInState}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <PortInAction
                                                            getPortInDetails={getPortInDetails}
                                                            handleEditPortIn={handlePortIn}
                                                            portInId={portInDetails?.mnpId}
                                                            portInState={portInState}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            )}
                                        </Fragment>
                                    )}
                                </HandleErrorAndLoading>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </AccountDetailsLayout>
    );
};
